import type { BaseItemContent } from "@memorang/types/src/session/new-session";
import { lucideToMdiIcon } from "../../../helpers/lucide-to-mdi";
import type { RawProethicaSessionItem } from "./proethica";

type NewFormatMedia = {
	type: string;
	imageUrl?: string;
	videoUrl?: string;
	transcript?: string;
	transcriptInstruction?: string;
	transcriptTitle?: string;
};

type ModuleOverview = {
	moduleNumber: number;
	title: string;
	summary: string;
	estimatedDurationMinutes: number;
	activityCount: number;
	knowledgeCheckCount: number;
};

type ModuleIntroContent = {
	overview: ModuleOverview;
	moduleOutline: {
		heading: string;
		sections: {
			title: string;
			items?: { title: string; icon?: string }[];
		}[];
	};
	learningObjectives: { heading: string; objectives: string[] };
};

type NewFormatChoice = {
	id: string;
	text: string;
	description?: string;
	icon?: string;
};

type NewFormatResponse = {
	type: string;
	choices: NewFormatChoice[];
	instruction?: string;
};

type NewFormatScoring = {
	answerKey?: { choiceId: string; isCorrect: boolean }[];
};

type NewFormatFeedback = {
	choiceId?: string;
	detailed?: { text?: string };
};

export type NewFormatModuleItem = {
	id: number;
	metadata: {
		ui_type: string;
		position: number;
		language?: string;
		sourceId?: number;
		scenarioId?: string;
	};
	content?: unknown;
	response?: NewFormatResponse;
	scoring?: NewFormatScoring;
	feedback?: NewFormatFeedback[];
};

/** Maps a single new-format item to a legacy raw session item. */
export type ModuleNormalizer = (
	item: NewFormatModuleItem,
) => RawProethicaSessionItem;

const itemUiType = (item: NewFormatModuleItem) => item.metadata.ui_type;

// Pre/post-test items are flat MCQs tagged with this ui_type instead of a
// module activity type; they share the knowledge-check shape (content.stem,
// response.choices, scoring.answerKey, feedback) so they reuse its normalizer.
export const PROETHICA_TEST_UI_TYPE = "pre-test post-test question";

export const isProethicaTestItem = (item: NewFormatModuleItem): boolean =>
	item.metadata?.ui_type === PROETHICA_TEST_UI_TYPE;

const byPosition = (a: NewFormatModuleItem, b: NewFormatModuleItem) =>
	a.metadata.position - b.metadata.position;

const LETTER_CHOICE_ID = /^[A-Z]$/;

// Feedback is keyed by the choice letter ("A"/"B"/...) while choices and
// answerKey use UUIDs, so per-choice explanations would be dropped by the UUID
// lookups below. Realign letter-keyed feedback onto the choice UUIDs by
// position; feedback already keyed by a UUID passes through unchanged.
const remapLetterFeedback = (
	item: NewFormatModuleItem,
): NewFormatModuleItem => {
	const choices = item.response?.choices;
	if (!(item.feedback?.length && choices?.length)) {
		return item;
	}
	return {
		...item,
		feedback: item.feedback.map((entry) => {
			const cid = entry.choiceId;
			const choice =
				cid && LETTER_CHOICE_ID.test(cid)
					? choices[cid.charCodeAt(0) - 65]
					: undefined;
			return choice ? { ...entry, choiceId: choice.id } : entry;
		}),
	};
};

const stripBold = (text: string) => text.replace(/\*\*/g, "");

const toVideoMedia = (media: NewFormatMedia, tabTitle?: string) => ({
	type: media.type,
	url: media.videoUrl ?? media.imageUrl ?? "",
	title: tabTitle,
	transcript: media.transcript,
	instruction: media.transcriptInstruction,
	buttonText: media.transcriptTitle,
});

const firstImageUrl = (media: NewFormatMedia[] | undefined) =>
	media?.find((m) => m.imageUrl)?.imageUrl;

const toImageUrls = (
	media: { type: string; imageUrl?: string }[] | undefined,
): { url: string }[] =>
	(media ?? [])
		.filter((m) => m.type.toUpperCase() === "IMAGE")
		.map((m) => ({ url: m.imageUrl ?? "" }));

// When a scenario carries example media, the first paragraph (which ends with
// "A specific example is below:") becomes the stem and the rest renders as
// `textAfter`, so the image sits between the two paragraphs instead of below
// both.
const splitTextAroundMedia = (
	text: string,
	media: NewFormatMedia[] | undefined,
) => {
	const hasImage = media?.some((m) => m.type.toUpperCase() === "IMAGE");
	if (!hasImage) {
		return { text };
	}
	const paragraphs = text.split(/\n{2,}/);
	if (paragraphs.length < 2) {
		return { text };
	}
	const [first, ...rest] = paragraphs;
	return { text: first.trim(), textAfter: rest.join("\n\n").trim() };
};

const isCorrectById = (item: NewFormatModuleItem) => {
	const map = new Map(
		(item.scoring?.answerKey ?? []).map((entry) => [
			entry.choiceId,
			entry.isCorrect,
		]),
	);
	return (choiceId: string) => map.get(choiceId) ?? false;
};

const feedbackByChoiceId = (item: NewFormatModuleItem, choiceId: string) =>
	item.feedback?.find((f) => f.choiceId === choiceId)?.detailed?.text;

const normalizeModuleIntro: ModuleNormalizer = (item) => {
	const c = item.content as ModuleIntroContent;

	return {
		id: String(item.id),
		type: "MODULE_LANDING",
		position: item.metadata.position,
		content: {
			lesson: { heading: "Lesson", text: c.overview.summary },
			outline: {
				heading: c.moduleOutline.heading,
				sections: c.moduleOutline.sections.map((section) => ({
					title: section.title,
					items: (section.items ?? []).map((entry) => ({
						title: entry.title,
						icon: lucideToMdiIcon(entry.icon),
					})),
				})),
			},
			learningObjectives: {
				heading: c.learningObjectives.heading,
				intro: "",
				items: c.learningObjectives.objectives,
			},
		},
	};
};

const normalizeScenarioPresentation: ModuleNormalizer = (item) => {
	const c = item.content as {
		title: string;
		text: string;
		instruction?: string;
		learn?: string;
		scenario?: string;
		media?: NewFormatMedia[];
		steps?: { title: string; description: string }[];
	};
	const id = String(item.id);
	const position = item.metadata.position;
	const media = (c.media ?? []).map((m) => toVideoMedia(m));

	if (c.steps?.length) {
		return {
			id,
			type: "SCENARIO",
			position,
			title: c.title,
			instruction: "",
			content: {
				text: c.text,
				media,
				steps: c.steps.map((step) => ({
					title: stripBold(step.title),
					description: step.description,
				})),
			},
		};
	}

	if (c.learn) {
		return {
			id,
			type: "LESSON",
			position,
			title: c.title,
			instruction: c.instruction ?? "",
			content: { text: c.text, lesson: c.learn },
		};
	}

	if (c.scenario) {
		return {
			id,
			type: "SCENARIO",
			position,
			title: c.title,
			instruction: c.instruction ?? "",
			content: {
				scenarioPractice: {
					info: c.text,
					scenario: c.scenario,
					instruction: c.instruction,
					media: toImageUrls(c.media),
				},
			},
		};
	}

	return {
		id,
		type: "SCENARIO",
		position,
		title: c.title,
		instruction: c.instruction ?? "",
		content: {
			...splitTextAroundMedia(c.text, c.media),
			media,
		},
	};
};

// An explorer that lists ethical principles without an attached question
// (Module 1).
const normalizePrincipleExplorer: ModuleNormalizer = (item) => {
	const c = item.content as {
		title: string;
		instruction?: string;
		ethicalPrinciples: {
			title: string;
			items: { title: string; description: string }[];
		};
	};

	return {
		id: String(item.id),
		type: "EXPLORE",
		position: item.metadata.position,
		title: c.title,
		instruction: c.instruction ?? "",
		content: {
			stem: "",
			principlesLayout: "tabs",
			principlesHeading: c.ethicalPrinciples.title,
			principles: c.ethicalPrinciples.items.map((principle) => ({
				title: stripBold(principle.title),
				description: principle.description,
			})),
		},
	};
};

const normalizePerspectiveResourceSet: ModuleNormalizer = (item) => {
	const c = item.content as {
		title: string;
		text: string;
		instruction?: string;
		items: { title: string; media: NewFormatMedia }[];
	};

	return {
		id: String(item.id),
		type: "SCENARIO",
		position: item.metadata.position,
		title: c.title,
		instruction: c.instruction ?? "",
		content: {
			text: c.text,
			media: c.items.map((entry) => toVideoMedia(entry.media, entry.title)),
		},
	};
};

const normalizeReflect: ModuleNormalizer = (item) => {
	const c = item.content as {
		title: string;
		text: string;
		question: string;
		instruction?: string;
	};
	const correct = isCorrectById(item);

	return {
		id: String(item.id),
		type: "REFLECT",
		position: item.metadata.position,
		title: c.title,
		instruction: c.instruction ?? "",
		content: {
			stem: `${c.text}\n\n${c.question}`,
			options: (item.response?.choices ?? []).map((choice) => ({
				id: choice.id,
				text: choice.text,
				description: choice.description,
				isCorrect: correct(choice.id),
				feedback: feedbackByChoiceId(item, choice.id),
			})),
		},
	};
};

const normalizePrincipleSataActivity: ModuleNormalizer = (item) => {
	const c = item.content as {
		title: string;
		text: string;
		instruction?: string;
		ethicalPrinciples: {
			title: string;
			items: { title: string; description: string }[];
		};
		question: string;
	};
	const correct = isCorrectById(item);
	const isMultiSelect = item.response?.type === "MULTIPLE_SELECT";

	return {
		id: String(item.id),
		type: "EXPLORE",
		position: item.metadata.position,
		title: c.title,
		instruction: c.instruction ?? "",
		content: {
			stem: c.text,
			principlesHeading: c.ethicalPrinciples.title,
			principles: c.ethicalPrinciples.items.map((principle) => ({
				title: stripBold(principle.title),
				description: principle.description,
			})),
			question: {
				type: isMultiSelect ? "SATA" : "SINGLE_SELECT",
				stem: c.question,
				options: (item.response?.choices ?? []).map((choice) => ({
					id: choice.id,
					text: choice.text,
					isCorrect: correct(choice.id),
				})),
			},
		},
	};
};

const formatRiskBack = (back: {
	riskLevel?: string;
	explanation?: string;
}) => {
	const riskLevel = back.riskLevel ?? "";
	const explanation = back.explanation ?? "";
	return explanation && explanation !== riskLevel
		? `**${riskLevel}:** ${explanation}`
		: `**${riskLevel}**`;
};

const normalizePrincipleReviewCards: ModuleNormalizer = (item) => {
	const c = item.content as {
		title: string;
		text: string;
		instruction?: string;
		items: {
			front: { title: string; media?: NewFormatMedia[] };
			back: { riskLevel?: string; explanation?: string };
		}[];
	};

	return {
		id: String(item.id),
		type: "REVIEW_FLASHCARD",
		position: item.metadata.position,
		title: c.title,
		instruction: c.instruction ?? "",
		content: {
			stem: c.text,
			cards: c.items.map((card) => ({
				front: {
					title: card.front.title,
					imageUrl: firstImageUrl(card.front.media),
				},
				back: { content: formatRiskBack(card.back) },
			})),
		},
	};
};

const buildAnalysisBack = (entry: {
	analysis?: { title?: string; text?: string };
	questions?: { title?: string; text?: string };
	keyTakeaway?: { title?: string; text?: string };
}) =>
	(
		[
			["ANALYSIS", entry.analysis],
			["QUESTIONS", entry.questions],
			["KEY TAKEAWAY", entry.keyTakeaway],
		] as const
	)
		.filter(([, section]) => Boolean(section?.text))
		.map(([marker, section]) => `${marker}\n\n${section?.text ?? ""}`.trim())
		.join("\n\n");

const normalizeAnalysisCards: ModuleNormalizer = (item) => {
	const c = item.content as {
		title: string;
		text?: string;
		instruction?: string;
		items: {
			title: string;
			media?: NewFormatMedia[];
			analysis?: { title?: string; text?: string };
			questions?: { title?: string; text?: string };
			keyTakeaway?: { title?: string; text?: string };
		}[];
	};

	return {
		id: String(item.id),
		type: "ANALYSIS_FLASHCARD",
		position: item.metadata.position,
		title: c.title,
		instruction: c.instruction ?? "",
		content: {
			stem: c.text ?? "",
			cards: c.items.map((entry) => ({
				front: {
					title: entry.title,
					imageUrl: firstImageUrl(entry.media),
				},
				back: { content: buildAnalysisBack(entry) },
			})),
		},
	};
};

type EthicalSituationContent = {
	title: string;
	text?: string;
	instruction?: string;
	situation: {
		title: string;
		setup: string;
		prompt?: string;
		cards: { id: string; label: string; back: { text: string } }[];
	};
};

const groupEthicalSituations = (
	items: NewFormatModuleItem[],
): RawProethicaSessionItem => {
	const first = items[0];
	const firstContent = first.content as EthicalSituationContent;

	return {
		id: String(first.id),
		type: "ETHICAL_SITUATION",
		position: first.metadata.position,
		title: firstContent.title,
		instruction: firstContent.instruction ?? "",
		content: {
			cards: items.map((item) => {
				const s = (item.content as EthicalSituationContent).situation;
				return {
					front: {
						title: s.title,
						situation: s.prompt ? `${s.setup}\n\n${s.prompt}` : s.setup,
					},
					back: {
						outcomes: s.cards.map((card) => ({
							id: card.id,
							label: card.label,
							content: card.back.text,
						})),
					},
				};
			}),
		},
	};
};

export const normalizeKnowledgeCheck: ModuleNormalizer = (item) => {
	const c = item.content as {
		title: string;
		stem: { text: string };
	};
	const correct = isCorrectById(item);
	const correctChoiceId = item.scoring?.answerKey?.find(
		(entry) => entry.isCorrect,
	)?.choiceId;

	return {
		id: String(item.id),
		type: "MCQ",
		position: item.metadata.position,
		title: c.title,
		instruction: item.response?.instruction ?? "",
		feedback: correctChoiceId
			? feedbackByChoiceId(item, correctChoiceId)
			: item.feedback?.[0]?.detailed?.text,
		content: {
			stem: c.stem.text,
			options: (item.response?.choices ?? []).map((choice) => ({
				id: choice.id,
				text: choice.text,
				isCorrect: correct(choice.id),
			})),
		},
	};
};

const normalizeModuleSummary: ModuleNormalizer = (item) => {
	const c = item.content as { title: string; text: string };

	return {
		id: String(item.id),
		type: "MODULE_ENDING",
		position: item.metadata.position,
		title: c.title,
		content: { text: c.text },
	};
};

type PerspectiveCardsContent = {
	title: string;
	text: string;
	instruction?: string;
	cards: {
		id: string;
		label: string;
		front: { text: string };
		back: { title: string; text: string };
		media?: NewFormatMedia[];
	}[];
};

const normalizePerspectiveCards: ModuleNormalizer = (item) => {
	const c = item.content as PerspectiveCardsContent;

	return {
		id: String(item.id),
		type: "PERSPECTIVE",
		position: item.metadata.position,
		title: c.title,
		instruction: c.instruction ?? "",
		content: {
			text: c.text,
			perspectives: c.cards.map((card) => ({
				perspective: card.front.text,
				principle: `${stripBold(card.back.title)}\n\n${card.back.text}`,
			})),
		},
	};
};

type SimulationMedia = { type: string; imageUrl: string };

export type SimulationScenarioContent = {
	title: string;
	scenario: { text: string; media?: SimulationMedia[] };
	objectives: string[];
	persona: { name: string; role: string; description: string };
	prompt: string;
};

export type SimulationScoring = {
	type: "AI_RUBRIC";
	criteria: Record<
		string,
		{
			name: string;
			icon: string;
			description: string;
			points: { min: number; max: number };
		}
	>;
	points: number;
};

export type SimulationScenarioItem = Omit<NewFormatModuleItem, "scoring"> & {
	content: SimulationScenarioContent;
	scoring?: SimulationScoring;
};

type SimulationContent = NonNullable<BaseItemContent["simulation"]>;

const toReflectSkills = (
	scoring: SimulationScoring | undefined,
): SimulationContent["reflectSkills"] =>
	scoring?.type === "AI_RUBRIC"
		? Object.values(scoring.criteria).map((criterion) => ({
				name: criterion.name,
				icon: lucideToMdiIcon(criterion.icon),
				description: criterion.description,
				max: criterion.points.max,
			}))
		: [];

// Rides on the session item so the simulation feature reads scenario data off
// the session, not the module JSON — keeps the live-API swap drop-in.
const buildSimulationContent = (
	item: SimulationScenarioItem,
): SimulationContent => {
	const c = item.content;
	return {
		screenTitle: c.title,
		prompt: c.prompt,
		persona: c.persona,
		scenarioText: c.scenario.text,
		scenarioMedia: toImageUrls(c.scenario.media),
		objectives: c.objectives,
		reflectSkills: toReflectSkills(item.scoring),
	};
};

const normalizeSimulationScenario: ModuleNormalizer = (item) => {
	const simItem = item as SimulationScenarioItem;

	return {
		id: String(item.id),
		type: "AI_SIMULATION",
		position: item.metadata.position,
		title: simItem.content.title,
		scenarioId: item.metadata.scenarioId,
		content: { simulation: buildSimulationContent(simItem) },
	};
};

const normalizeSimulationFeedback: ModuleNormalizer = (item) => {
	const c = item.content as { title: string; instruction?: string };

	return {
		id: String(item.id),
		type: "AI_SIMULATION_REPORT",
		position: item.metadata.position,
		title: c.title,
		instruction: c.instruction ?? "",
		scenarioId: item.metadata.scenarioId,
		content: {},
	};
};

/** Activity type → normalizer, shared by every module. */
const defaultNormalizers: Record<string, ModuleNormalizer> = {
	module_intro: normalizeModuleIntro,
	scenario_presentation: normalizeScenarioPresentation,
	principle_explorer: normalizePrincipleExplorer,
	scenario_perspective_resource_set: normalizePerspectiveResourceSet,
	scenario_perspective_cards: normalizePerspectiveCards,
	scenario_reflect_framework_choice: normalizeReflect,
	scenario_principle_sata_activity: normalizePrincipleSataActivity,
	scenario_principle_review_cards: normalizePrincipleReviewCards,
	scenario_analysis_cards: normalizeAnalysisCards,
	simulation_scenario: normalizeSimulationScenario,
	simulation_feedback: normalizeSimulationFeedback,
	knowledge_check_question: normalizeKnowledgeCheck,
	module_summary: normalizeModuleSummary,
};

export type NormalizeModuleOptions = {
	/** Per-module normalizers, keyed by activity type; override the defaults. */
	overrides?: Record<string, ModuleNormalizer>;
	/** Fallback for activity types absent from the registry. */
	defaultNormalizer?: ModuleNormalizer;
};

export const normalizeModuleItems = (
	items: NewFormatModuleItem[],
	options: NormalizeModuleOptions = {},
): RawProethicaSessionItem[] => {
	const normalizers = { ...defaultNormalizers, ...options.overrides };
	const sorted = [...items].map(remapLetterFeedback).sort(byPosition);
	const ethicalSituations = sorted.filter(
		(item) => itemUiType(item) === "ethical_situation",
	);

	const result: RawProethicaSessionItem[] = [];
	let ethicalGrouped = false;

	for (const item of sorted) {
		const uiType = itemUiType(item);
		if (uiType === "ethical_situation") {
			if (!ethicalGrouped) {
				result.push(groupEthicalSituations(ethicalSituations));
				ethicalGrouped = true;
			}
			continue;
		}

		const normalize = normalizers[uiType] ?? options.defaultNormalizer;
		if (normalize) {
			result.push(normalize(item));
		}
	}

	return result;
};

export const findModuleOverview = (
	items: NewFormatModuleItem[],
): ModuleOverview | undefined =>
	(
		items.find((item) => itemUiType(item) === "module_intro")?.content as
			| ModuleIntroContent
			| undefined
	)?.overview;
